import React from 'react'

import { cannot } from '@/app/helpers'
import { useCampaignParts } from '@/app/module/campaigns/component/hooks/use-campaign-parts'
import { GetVoiceProps } from '@/app/module/campaigns/component/item/steps/content/part-config-props'
import { LocalActionType, LocalNonEmptyAction } from '@/app/module/campaigns/types'

import { ActionsBoxProps } from './actions-box-props'
import APIDialog from './dialog/api'
import JumpDialog from './dialog/jump-to-question'
import PickupDialog from './dialog/pickup'
import ReopenDialog from './dialog/reopen'
import SendEmailDialog from './dialog/send-email'
import SendSMSDialog from './dialog/sms'
import SendSMSToPhoneDialog from './dialog/sms-to-phone-numbers'
import SubscribeToCampaignDialog from './dialog/subscribe-to-campaign'
import TopupDialog from './dialog/topup'
import TransferDialog from './dialog/transfer'
import UpdateContactDialog from './dialog/update-contact'
import VoiceDialog from './dialog/voice'
import SendWhatsAppDialog from './dialog/whatsapp'

type Props = Pick<
  ActionsBoxProps,
  | 'countriesTimezones'
  | 'countryCode'
  | 'defaultLanguage'
  | 'defaultSenderId'
  | 'hasTranscribe'
  | 'imageId'
  | 'index'
  | 'languages'
  | 'personalizationList'
  | 'phoneNumber'
  | 'senderIds'
  | 'timezone'
  | 'timezones'
  | 'transcribeEnabled'
  | 'updateContactConfig'
  | 'voiceId'
  | 'getImageProps'
  | 'getVoiceProps'
> & {
  action: LocalNonEmptyAction
  isNewAction: boolean

  getVoiceProps: GetVoiceProps
  onClose: () => void
  onSave: (action: Partial<LocalNonEmptyAction>) => void
}

const ActionDialogWrapper: React.FC<Props> = ({
  action,
  countriesTimezones,
  countryCode,
  defaultLanguage,
  defaultSenderId,
  hasTranscribe,
  imageId,
  index,
  isNewAction,
  languages,
  personalizationList,
  phoneNumber,
  senderIds,
  timezone,
  timezones,
  transcribeEnabled,
  updateContactConfig,
  voiceId,
  getImageProps,
  getVoiceProps,
  onClose,
  onSave,
}) => {
  const parts = useCampaignParts()

  const commonProps = { isNewAction, onClose, onSave }

  const imageProps = React.useMemo(() => getImageProps(`${imageId}-${index}`), [index, imageId, getImageProps])
  const voiceProps = React.useMemo(() => getVoiceProps(`${voiceId}-${index}`), [index, voiceId, getVoiceProps])

  switch (action.type) {
    case LocalActionType.API:
      return <APIDialog {...commonProps} personalizationList={personalizationList} value={action} />

    case LocalActionType.Pickup:
      return <PickupDialog {...commonProps} value={action} />

    case LocalActionType.Jump:
      return <JumpDialog {...commonProps} index={index} parts={parts} value={action} />

    case LocalActionType.PlayAudio:
      return (
        <VoiceDialog
          {...commonProps}
          defaultLanguage={defaultLanguage}
          languages={languages}
          value={action}
          voiceProps={voiceProps}
        />
      )

    case LocalActionType.Reopen:
      return <ReopenDialog {...commonProps} value={action} />

    case LocalActionType.SendEmail:
      return <SendEmailDialog {...commonProps} personalizationList={personalizationList} value={action} />

    case LocalActionType.SendSMS:
      return (
        <SendSMSDialog
          {...commonProps}
          defaultLanguage={defaultLanguage}
          defaultSenderId={defaultSenderId}
          languages={languages}
          personalizationList={personalizationList}
          phoneNumber={phoneNumber}
          senderIds={senderIds}
          value={action}
        />
      )

    case LocalActionType.SendSMSToPhoneNumbers:
      return (
        <SendSMSToPhoneDialog
          {...commonProps}
          defaultLanguage={defaultLanguage}
          defaultSenderId={defaultSenderId}
          languages={languages}
          personalizationList={personalizationList}
          phoneNumber={phoneNumber}
          senderIds={senderIds}
          value={action}
        />
      )

    case LocalActionType.SendWhatsApp:
      return (
        <SendWhatsAppDialog
          {...commonProps}
          defaultLanguage={defaultLanguage}
          imageProps={imageProps}
          languages={languages}
          personalizationList={personalizationList}
          value={action}
          voiceProps={voiceProps}
        />
      )

    case LocalActionType.SubscribeToCampaign:
      return (
        <SubscribeToCampaignDialog
          {...commonProps}
          countriesTimezones={countriesTimezones}
          countryCode={countryCode}
          timezone={timezone}
          timezones={timezones}
          value={action}
        />
      )

    case LocalActionType.Topup:
      return <TopupDialog {...commonProps} personalizationList={personalizationList} value={action} />

    case LocalActionType.Transfer:
      return (
        <TransferDialog
          {...commonProps}
          countriesTimezones={countriesTimezones}
          countryCode={countryCode}
          value={action}
        />
      )

    case LocalActionType.UpdateContact:
      return (
        <UpdateContactDialog
          {...commonProps}
          invalid={updateContactConfig?.invalid || false}
          openResponse={updateContactConfig?.openResponse || false}
          question={updateContactConfig?.question || false}
          transcribeAvailable={hasTranscribe}
          transcribeEnabled={transcribeEnabled}
          type={updateContactConfig?.type}
          personalizationList={personalizationList}
          value={action}
        />
      )

    case LocalActionType.FinishFlow:
    case LocalActionType.Hangup:
    case LocalActionType.Replay:
    case LocalActionType.Resend:
    case LocalActionType.RunQuota:
    case LocalActionType.Unsubscribe:
    case LocalActionType.UnsubscribeAll: // those actions do not have dialog
      return null

    default:
      return cannot(action)
  }
}

export default ActionDialogWrapper
